import { Box, Button, formatFileSize, Grid, RichTextReader, Stack, Typography } from "@compass/core";
import { DefaultExtensionType, FileIcon as ReactFileIcon, defaultStyles } from "react-file-icon";
import PreviewIcon from "@mui/icons-material/Preview";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ApplicationResponseFile, StudentServiceFile } from "generated/openApi";
import ApiService from "services/apiService";

export type FileListItemProps = {
  file: ApplicationResponseFile | StudentServiceFile;
  fileCategory?: string;
  index: Number;
  onDelete?: () => void;
  deleting?: boolean;
};

export const FileListItem = ({
  file,
  fileCategory,
  index,
  onDelete,
  deleting,
}: FileListItemProps) => {
  const iconWidth = "35px";
  const thumbnailSize = "42px";
  const extension: string | undefined = file.uploadFile?.fileName.split(".").pop();

  const getFile = async () => {
    const open = true;
    if (file.uploadFile.fileRef && file.uploadFile.fileName) {
      const fileRef = file.uploadFile.fileRef;
      const fileName = file.uploadFile.fileName;

      ApiService.common.getFile(fileRef, fileName).then((response) => {
        const blob = response!.data;
        const url = window.URL.createObjectURL(blob);

        if (open) {
          window.open(url, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
    }
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) auto" }, gap: 2, alignItems: "start" }}>
          <Stack direction="row" spacing="sm" alignItems="center" sx={{ minWidth: 0 }}>
            <Box display="inline-flex" width={iconWidth} height={thumbnailSize} sx={{ flexShrink: 0 }}>
              <ReactFileIcon extension={extension} {...defaultStyles[extension as DefaultExtensionType]} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight="medium" sx={{ overflowWrap: "anywhere" }}>
                {file.uploadFile?.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{formatFileSize(file.uploadFile?.fileSize)}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing="xs" sx={{ flexShrink: 0 }}>
            <Button variant="outlined" startIcon={<PreviewIcon />} onClick={() => { getFile() }}>
              View
            </Button>
            {onDelete && (
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete} disabled={deleting} aria-label={`Delete ${file.uploadFile?.fileName}`}>
                Delete
              </Button>
            )}
          </Stack>
        </Box>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 2fr)" },
          gap: 2,
          mt: 2,
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography sx={{ overflowWrap: "anywhere" }}>
              {fileCategory || "N/A"}
            </Typography>
          </Box>
          <Box sx={{
            minWidth: 0,
            overflowWrap: "anywhere",
            overflowX: "auto",
            "& img, & video": { maxWidth: "100%", height: "auto" },
            "& p": { margin: 0 },
          }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <RichTextReader html={file.note} />
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};
